import { apiRequest, graphConfig, loginRequest } from '../components/Auth/authConfig';
import { msalInstance } from '../components/Auth/MsalProvider';
import { VoucherFormState } from '../components/VoucherForm/Context';
import { GetVouchersResponse } from '../types/GetVouchers';
import { Batch, Voucher } from '../types/Voucher';
import { VoucherComment } from '../types/Comments';
import { Line } from '../types/Line';
import { VoucherFilter } from '../types/VoucherFilter';
import { PopupRequest } from '@azure/msal-browser';
import { BatchUpdatePayload } from '../types/BatchUpdate';
import { VoucherLinkedOrder } from '../types/VoucherLinkedOrder';
import { CancelVouchersByCSV, CancelVouchersByCSVStatus } from '../types/CancelVouchers';

const VOUCHER_SERVICE_URL =
  process.env.REACT_APP_VOUCHER_SERVICE_API_BASE_URL || 'http://localhost:8081';

export async function getBackendApiToken(requestType: PopupRequest) {
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error('No active account. Please sign in first.');

  const response = await msalInstance.acquireTokenSilent({ ...requestType, account });

  return response.accessToken;
}

async function fetchWithAuth(url: string, options: RequestInit): Promise<Response> {
  const token = await getBackendApiToken(apiRequest);
  const headers = new Headers(options.headers);

  headers.set('Authorization', `Bearer ${token}`);
  options.mode = 'cors';
  options.headers = headers;

  return fetch(url, options);
}

export async function callMsGraph() {
  const token = await getBackendApiToken(loginRequest);

  return fetch(graphConfig.graphMeEndpoint, { headers: { Authorization: `Bearer ${token}` } })
    .then(response => response.json())
    .catch(console.error); // eslint-disable-line no-console
}

export async function getVouchers(filters: VoucherFilter): Promise<GetVouchersResponse> {
  const params = new URLSearchParams();

  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.code) params.append('code', filters.code);
  if (filters.createdBy) params.append('createdBy', filters.createdBy);
  if (filters.status) params.append('status', filters.status);

  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/vouchers?${params}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getVoucher(id: string): Promise<Voucher> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher-by-id/${id}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

async function requestWithAuth<T, P = undefined>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
  payload?: P, // Generic payload type
): Promise<T> {
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (payload && ['POST', 'PUT', 'PATCH'].includes(method)) {
    options.body = JSON.stringify(payload);
  }

  const response = await fetchWithAuth(url, options);
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export const generateVoucher = (payload: VoucherFormState['payload']) =>
  requestWithAuth<Voucher, VoucherFormState['payload']>(
    `${VOUCHER_SERVICE_URL}/api/voucher/generate`,
    'POST',
    payload,
  );

export const generateMultipleVouchers = (payload: VoucherFormState['payload']) =>
  requestWithAuth<Voucher, VoucherFormState['payload']>(
    `${VOUCHER_SERVICE_URL}/api/voucher/generate-multiple`,
    'POST',
    payload,
  );

export const fetchVoucherBatch = (payload: BatchUpdatePayload) =>
  requestWithAuth<Voucher, BatchUpdatePayload>(
    `${VOUCHER_SERVICE_URL}/api/voucher/batch`,
    'PUT',
    payload,
  );

export async function getBatch(id: string): Promise<Batch> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/batch/${id}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getBatchDownloadData(id: string): Promise<string> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/batch/${id}/export`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/csv',
      Accept: 'text/csv',
    },
  });
  if (!response.ok) throw new Error(await response.text());

  return response.text();
}

export async function getLines(q: string): Promise<Line[]> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/lines/autocomplete?q=${q}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getLineById(lineId: string): Promise<Line> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/lines/${lineId}`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function getLinkedOrders(id: string): Promise<VoucherLinkedOrder[]> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/${id}/orders`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export const saveComment = (voucherId: Voucher['id'], payload: { comment: string }) =>
  requestWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/${voucherId}/comment`, 'POST', payload);

export async function getVoucherComments(id: string): Promise<VoucherComment[]> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/${id}/comments`, {
    method: 'GET',
  });
  if (!response.ok) throw new Error(await response.text());

  return response.json();
}

export async function updateVoucherComment(payload: {
  voucherId: string;
  comment: string;
  commentId?: string;
}): Promise<VoucherComment> {
  const { voucherId, ...body } = payload;

  return requestWithAuth<VoucherComment, typeof body>(
    `${VOUCHER_SERVICE_URL}/api/voucher/${voucherId}/comment`,
    'POST',
    body,
  );
}

export async function cancelVouchersByFile(form: FormData): Promise<CancelVouchersByCSV> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/file/cancel`, {
    method: 'POST',
    body: form,
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  return response.json();
}

export async function getCancelVouchersByFileStatus(
  id: string,
): Promise<CancelVouchersByCSVStatus | { csv: string }> {
  const response = await fetchWithAuth(`${VOUCHER_SERVICE_URL}/api/voucher/file/cancel/${id}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const contentType = response.headers.get('Content-Type') || '';

  if (contentType.includes('text/csv')) {
    return { csv: await response.text() };
  }

  try {
    return (await response.json()) as CancelVouchersByCSVStatus;
  } catch {
    return { csv: await response.text() };
  }
}
